import React, { useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { BookOpen, Lightbulb, Headphones, MoreHorizontal } from 'lucide-react';
import type { SidebarItemConfig } from './sidebar-config';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

const moreMenuItems = (
  t: TFunction<'pleri/chat'>,
): Array<{
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}> => [
  { id: 'chat-with-docs', label: t('chatWithDocs'), icon: BookOpen },
  { id: 'feature-request', label: t('featureRequest'), icon: Lightbulb },
  { id: 'contact-support', label: t('contactSupport'), icon: Headphones },
];

interface MorePopoverSidebarItemProps {
  item: SidebarItemConfig;
  isCollapsed: boolean;
  showLabelBelow?: boolean;
  itemCallbacks?: Record<string, () => void>;
  onItemClick?: (itemId: string) => void;
}

/**
 * Single "More" sidebar item that opens a popover with Chat with docs, Feature request, Contact support.
 */
export const MorePopoverSidebarItem: React.FC<MorePopoverSidebarItemProps> = ({
  item,
  isCollapsed,
  showLabelBelow = false,
  itemCallbacks,
  onItemClick,
}) => {
  const { t } = useTranslation('pleri/chat');
  const [open, setOpen] = useState(false);

  const iconClasses =
    'h-5 w-5 stroke-[1.8] transition-colors duration-150 text-gray-900 dark:text-gray-100';
  const iconClassesWithScale =
    'h-5 w-5 stroke-[1.8] transition-transform duration-150 group-hover/item:scale-110 text-gray-900 dark:text-gray-100';
  const miniIconWrapperClasses =
    'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-colors duration-150 group-hover/item:bg-gray-200/80';
  const miniWithLabelClasses =
    'group/item flex w-full flex-col items-center justify-center gap-1.5 rounded-lg border-0 bg-transparent py-1.5 px-1 min-w-0 transition-colors duration-150';
  const collapsedClasses =
    'group flex w-full items-center justify-center gap-2 rounded-[4.5px] p-2.5 hover:bg-gray-200 dark:hover:bg-gray-600 hover:shadow-sm w-[40px] h-[40px]';
  const expandedClasses =
    'group flex w-full items-center gap-2 rounded-[4.5px] px-2 py-2 hover:bg-gray-200 dark:hover:bg-gray-600';

  const handleMenuAction = (id: string) => {
    itemCallbacks?.[id]?.();
    onItemClick?.(id);
    setOpen(false);
  };

  const triggerContent =
    isCollapsed && showLabelBelow ? (
      <>
        <span className={miniIconWrapperClasses}>
          <MoreHorizontal className={iconClassesWithScale} />
        </span>
        <span className="line-clamp-2 max-w-full text-center text-xs font-medium text-gray-600 dark:text-gray-400">
          {item.label}
        </span>
      </>
    ) : isCollapsed ? (
      <MoreHorizontal className={iconClasses} />
    ) : (
      <>
        <MoreHorizontal className={iconClasses} />
        <span className="text-sm font-medium whitespace-nowrap text-gray-900 dark:text-gray-100">
          {item.label}
        </span>
      </>
    );

  const triggerClassName =
    isCollapsed && showLabelBelow
      ? miniWithLabelClasses
      : isCollapsed
        ? collapsedClasses
        : expandedClasses;

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          className={triggerClassName}
          data-testid={item.testId ?? 'more-nav-item'}
          aria-label={item.label}
        >
          {triggerContent}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          id="search-app"
          side="right"
          align="start"
          sideOffset={8}
          style={{ zIndex: 99999999999999 }}
        >
          <div className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=right]:slide-in-from-left-2 z-[99999999999999] w-[280px] rounded-lg border border-gray-200 bg-white p-0 shadow-lg dark:border-gray-700 dark:bg-gray-900">
            <div className="border-b border-gray-200 px-4 py-3 dark:border-gray-700">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {item.label}
              </h3>
            </div>
            <div className="py-1">
              {moreMenuItems(t).map((menuItem) => {
                const Icon = menuItem.icon;
                const callback = itemCallbacks?.[menuItem.id];
                return (
                  <button
                    key={menuItem.id}
                    type="button"
                    onClick={() => callback && handleMenuAction(menuItem.id)}
                    className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm text-gray-700 transition-colors hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-gray-100"
                    data-testid={`more-menu-${menuItem.id}`}
                  >
                    <Icon className="h-5 w-5 shrink-0 stroke-[1.8] text-gray-500 dark:text-gray-400" />
                    <span className="font-medium">{menuItem.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
